import React, { useContext, useEffect } from "react";
import { useSpring, animated, to } from "@react-spring/web";
import { useGesture } from "react-use-gesture";
import styled, { keyframes } from "styled-components";
import CardDetail from "./CardDetail";
import { CardContext } from "./context/CardContext";
import { SignInContext } from "./context/SignInContext";
import mysticalEyesDark from "../assets/mystical-eyes-dark.jpg";

// for card hover effect
const calcX = (y, ly) => -(y - ly - window.innerHeight / 2) / 20;
const calcY = (x, lx) => (x - lx - window.innerWidth / 2) / 20;

const Card = (props) => {
	const { onGetCard, domTarget, question, setQuestion } = props;
	const { flipped, cardData } = useContext(CardContext);
	const { signedIn } = useContext(SignInContext);
	const { transform, opacity } = useSpring({
		opacity: flipped ? 1 : 0,
		transform: `perspective(600px) rotateX(${flipped ? 180 : 0}deg)`,
		config: { mass: 5, tension: 300, friction: 100 },
	});

	useEffect(() => {
		const preventDefault = (e) => e.preventDefault();
		document.addEventListener("gesturestart", preventDefault);
		document.addEventListener("gesturechange", preventDefault);
		return () => {
			document.removeEventListener("gesturestart", preventDefault);
			document.removeEventListener("gesturechange", preventDefault);
		};
	}, []);

	const [{ x, y, rotateX, rotateY, rotateZ, zoom, scale }, api] = useSpring(
		() => ({
			rotateX: 0,
			rotateY: 0,
			rotateZ: 0,
			scale: 1,
			zoom: 0,
			x: 0,
			y: 0,
			config: { mass: 9, tension: 550, friction: 90 },
		})
	);

	useGesture(
		{
			onPinch: ({ offset: [d, a] }) => api.start({ zoom: d / 200, rotateZ: a }),
			onMove: ({ xy: [px, py], dragging }) =>
				!dragging &&
				api.start({
					rotateX: calcX(py, y.get()),
					rotateY: calcY(px, x.get()),
					scale: 1.1,
				}),
			onHover: ({ hovering }) =>
				!hovering && api.start({ rotateX: 0, rotateY: 0, scale: 1 }),
		},
		{ domTarget, eventOptions: { passive: false } }
	);

	return (
		<>
			<CardContainer
				disabled={!question || flipped}
				onClick={onGetCard}
				style={{ transform, opacity }}
			>
				<CardFrame
					ref={domTarget}
					style={{
						x,
						y,
						scale: to([scale, zoom], (s, z) => s + z),
						rotateX,
						rotateY,
						rotateZ,
						transform,
					}}
				>
					<CardBack style={{ opacity: opacity.to((o) => 1 - o) }}></CardBack>
					{cardData.cardImage ? (
						<>
							<CardFront
								src={cardData.cardImage}
								style={{
									opacity,
								}}
							/>
						</>
					) : (
						<>
							<CardFront
								src={mysticalEyesDark}
								style={{
									opacity,
								}}
							/>
							{flipped && (
								<CardUnavailableMessage>
									Reply hazy, please try again.
								</CardUnavailableMessage>
							)}
						</>
					)}
				</CardFrame>
			</CardContainer>
			<CardDetail question={question} setQuestion={setQuestion} />
			{!signedIn && (
				<SignInPrompt>
					Sign in at the top of the page to save your reading.
				</SignInPrompt>
			)}
		</>
	);
};

export default Card;

const CardContainer = styled.button`
	display: flex;
	justify-content: center;
	align-items: center;
	margin-top: 48px;
	width: 350px;
	height: 550px;
	border: none;
	background-color: transparent;
	&:disabled {
		cursor: initial;
		& * {
			cursor: initial;
		}
	}
`;

const CardFrame = styled(animated.div)`
	position: relative;
	width: 320px;
	height: 515px;
	border-radius: 5px;
	box-shadow: 0px 10px 30px -5px rgba(0, 0, 0, 0.3);
	transition: box-shadow 0.9s, opacity 0.9s;
	border: 10px solid white;
	cursor: grab;
	overflow: hidden;
	touch-action: none;
	transform: perspective(600px);
	will-change: transform, opacity;
	&:hover {
		box-shadow: 0px 30px 100px -10px rgba(0, 0, 0, 0.4);
	}
	& > div {
		will-change: transform;
		height: 100%;
		margin: 0vw 0;
		& > * {
			height: 100%;
			background-size: cover;
			background-position: center center;
			margin: 0vw 0;
		}
	}
`;

const CardBack = styled(animated.div)`
	background-image: url(${mysticalEyesDark});
	filter: invert(1);
	background-size: cover;
	background-repeat: no-repeat;
	background-position: center center;
	position: absolute;
	max-width: 320px;
	max-height: 515px;
	width: 320px;
	height: 515px;
	cursor: grab;
	will-change: transform, opacity;
`;

const CardFront = styled(animated.img)`
	max-width: 300px;
	max-height: 515px;
	width: 300px;
	height: 515px;
	cursor: grab;
	transform: rotateX(180deg);
	will-change: transform, opacity;
`;

const fadeIn = keyframes`
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
`;

const CardUnavailableMessage = styled.p`
	max-width: 200px;
	font-size: 3rem;
	font-weight: 600;
	color: #000;
	position: absolute;
	top: 159px;
	left: 51px;
	animation: ${fadeIn} 2s ease-in-out;
	transform: rotateX(180deg);
	background-color: #fff;
	border-radius: 40%;
	padding: 24px 8px;
	box-shadow: 0 0 16px 24px #fff;
`;

const SignInPrompt = styled.p`
	margin: 64px auto;
	font-size: 1.5rem;
	font-style: italic;
`;
